import React from "react";
import { Link } from "react-router-dom";
import { HiArrowNarrowRight, HiArrowNarrowDown } from "react-icons/hi";
import {
  HiOutlineBuildingOffice2,
  HiOutlineUserGroup,
  HiOutlineMegaphone,
  HiOutlineSpeakerWave,
  HiOutlineUserPlus,
  HiChevronRight,
} from "react-icons/hi2";

const DashboardContent = () => {
  const setupSteps = [
    {
      title: "Create Brands",
      description:
        "Create and manage your brands. Add brand details, track their performance, and manage their settings.",
      ctaLabel: "Create Brand",
      ctaPath: "/company/dashboard/brands",
      icon: HiOutlineBuildingOffice2,
    },
    {
      title: "Add Brand Manager",
      description:
        "Advertise your brand with brand manager's and start inviting your affiliate partners to maximize your reach and growth.",
      ctaLabel: "Add Brand",
      ctaPath: "/company/dashboard/brands/brand-managers",
      icon: HiOutlineUserGroup,
    },
    {
      title: "Create Campaign",
      description:
        "Launch a new marketing campaign. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
      ctaLabel: "Create Campaign",
      ctaPath: "/company/dashboard/campaigns",
      icon: HiOutlineMegaphone,
    },
    {
      title: "Create Campaign Ads",
      description:
        "Create and manage your campaign advertisements. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
      ctaLabel: "Create Ad",
      ctaPath: "/company/dashboard/campaigns/advertising/create",
      icon: HiOutlineSpeakerWave,
    },
    {
      title: "Add or Invite Affiliates",
      description:
        "Add or invite new affiliate partners to your network. Set up their profiles and commission structures.",
      ctaLabel: "Add & Invite",
      ctaPath: "/company/dashboard/affiliates",
      icon: HiOutlineUserPlus,
    },
  ];

  // Helper function to truncate content after 20 words
  const truncateText = (text, wordLimit) => {
    const words = text.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };

  return (
    <div className="flex-1 p-0 md:p-3 lg:p-4">
      <div className="bg-white border border-gray-100 shadow-sm p-2 mb-4 md:hidden">
        <h2 className="text-lg leading-7 font-bold mb-2">Quick setup guide</h2>
        <p className="text-xs text-gray-500 mb-4">
          You're literally minutes away from launching your partnership marketing
          program.
        </p>

        <div className="flex items-center justify-between mb-4">
          {setupSteps.map((_, index) => (
            <React.Fragment key={`mobile-step-${index + 1}`}>
              <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[11px] font-semibold flex items-center justify-center">
                {index + 1}
              </span>
              {index < setupSteps.length - 1 && (
                <HiArrowNarrowRight className="text-orange-500 text-lg shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="space-y-3">
          {setupSteps.map((step) => {
            const StepIcon = step.icon;

            return (
              <div
                key={step.title}
                className="rounded-xl border border-gray-200 bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
              >
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                    <StepIcon className="text-lg" />
                  </span>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-base font-semibold leading-5">{step.title}</h3>
                      <HiChevronRight className="text-gray-300 text-lg shrink-0 mt-0.5" />
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1.5 leading-4">
                      {truncateText(step.description, 18)}
                    </p>
                    <div className="mt-3 flex justify-end">
                      <Link
                        to={step.ctaPath}
                        className="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-[11px] font-semibold text-white"
                      >
                        {step.ctaLabel}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hidden md:block bg-white shadow-md rounded-lg p-4 lg:p-5 mb-10 lg:mb-0">
        <h2 className="text-lg font-bold mb-2">Quick setup guide</h2>
        <p className="text-[10px] lg:text-sm text-gray-500 mb-4 lg:mb-5">
          You're literally minutes away from launching your partnership
          marketing program.
        </p>

        {/* Setup Process Flow */}

        {/* Setup Process Flow */}
        <div className="flex flex-wrap sm:flex-row flex-col sm:items-center justify-start text-[10px] md:text-sm text-black font-semibold mb-4 lg:mb-6 gap-x-2 lg:gap-x-4 gap-y-2 lg:gap-y-4">
          {/* Step 1 */}
          <div className="flex items-center sm:flex-row flex-col sm:gap-x-3">
            <span className="flex items-center gap-2 text-[10px] md:text-xs font-semibold">
              <span className="w-4 h-4 text-[8px] lg:text-xs bg-orange-500 text-white flex items-center justify-center rounded-full hidden md:flex lg:flex">
                1
              </span>
              Create Brand
            </span>
            {/* Arrow */}
            <HiArrowNarrowRight className="hidden sm:inline text-orange-500 text-[10px] lg:text-xl font-bold" />
            <HiArrowNarrowDown className="inline sm:hidden text-orange-500 text-sm md:text-xl font-bold self-center" />
          </div>

          {/* Step 2 */}
          <div className="flex items-center sm:flex-row flex-col sm:gap-x-3">
            <span className="flex items-center gap-2 text-[10px] md:text-xs font-semibold">
              <span className="w-4 h-4 text-[8px] lg:text-xs bg-orange-500 text-white flex items-center justify-center rounded-full hidden md:flex lg:flex">
                2
              </span>
              Add Brand Manager
            </span>

            {/* Arrow */}
            <HiArrowNarrowRight className="hidden sm:inline text-orange-500 text-[10px] lg:text-xl font-bold" />
            <HiArrowNarrowDown className="inline sm:hidden text-orange-500 text-sm md:text-xl font-bold self-center" />
          </div>

          {/* Step 3 */}
          <div className="flex items-center sm:flex-row flex-col sm:gap-x-3">
            <span className="flex items-center gap-2 text-[10px] md:text-xs font-semibold">
              <span className="w-4 h-4 text-[8px] lg:text-xs bg-orange-500 text-white flex items-center justify-center rounded-full hidden md:flex lg:flex">
                3
              </span>
              Create Campaign
            </span>

            {/* Arrow */}
            <HiArrowNarrowRight className="hidden sm:inline text-orange-500 text-[10px] lg:text-xl font-bold" />
            <HiArrowNarrowDown className="inline sm:hidden text-orange-500 text-sm md:text-xl font-bold self-center" />
          </div>

          {/* Step 4 */}
          <div className="flex items-center sm:flex-row flex-col sm:gap-x-3">
            <span className="flex items-center gap-2 text-[10px] md:text-xs font-semibold">
              <span className="w-4 h-4 text-[8px] lg:text-xs bg-orange-500 text-white flex items-center justify-center rounded-full hidden md:flex lg:flex">
                4
              </span>
              Create Campaign Advertisement
            </span>
            {/* Arrow */}
            <HiArrowNarrowRight className="hidden sm:inline text-orange-500 text-[10px] lg:text-xl font-bold" />
            <HiArrowNarrowDown className="inline sm:hidden text-orange-500 text-sm md:text-xl font-bold self-center" />
          </div>
          
          {/* Step 5 */}
          <div className="flex items-center sm:flex-row flex-col sm:gap-x-3">
            <span className="flex items-center gap-2 text-[10px] md:text-xs font-semibold">
              <span className="w-4 h-4 text-[8px] lg:text-xs bg-orange-500 text-white flex items-center justify-center rounded-full hidden md:flex lg:flex">
                5
              </span>
              Add or Invite Affiliates
            </span>
          </div>
        </div>

        {/* Setup Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Step 1 */}
          <div className="p-5 border rounded-lg flex flex-col h-full">
            <h3 className="font-semibold mb-2 ">Create Brands</h3>
            <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
              {truncateText(
                "Create and manage your brands. Add brand details, track their performance, and manage their settings.",
                20
              )}
            </p>
            <Link
              to="/company/dashboard/brands"
              className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
            >
              Create Brand
            </Link>
          </div>

          {/* Step 2 */}
          <div className="p-5 border rounded-lg flex flex-col h-full">
            <h3 className="font-semibold mb-2">Add Brand Manager</h3>
            <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
              {truncateText(
                "Advertise your brand with brand manager's and start inviting your affiliate partners to maximize your reach and growth.",
                20
              )}
            </p>
            <Link
              to="/company/dashboard/brands/brand-managers"
              className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
            >
              Add Brand
            </Link>
          </div>

           {/* Step 3 */}
           <div className="p-5 border rounded-lg flex flex-col h-full">
            <h3 className="font-semibold mb-2">Create Campaign</h3>
            <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
              {truncateText(
                "Launch a new marketing campaign. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
                20
              )}
            </p>
            <Link
              to="/company/dashboard/campaigns"
              className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
            >
              Create Campaign
            </Link>
          </div>

          {/* Step 4 */}
          <div className="p-5 border rounded-lg flex flex-col h-full">
            <h3 className="font-semibold mb-2">Create Campaign Ads</h3>
            <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
              {truncateText(
                "Create and manage your campaign advertisements. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
                20
              )}
            </p>
            <Link
              to="/company/dashboard/campaigns/advertising/create"
              className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
            >
              Create Advertisement
            </Link>
          </div>

              {/* Step 5 */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
            <h3 className="font-semibold mb-2">Add or Invite Affiliates</h3>
            <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
              {truncateText(
                "Add or invite new affiliate partners to your network. Set up their profiles and commission structures.",
                20
              )}
            </p>
            <Link
              to="/company/dashboard/affiliates"
              className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
            >
              Add & Invite
            </Link>
          </div>

          {/* Step 6 */}
          {/* <div className="p-5 border rounded-lg flex flex-col h-full">
            <h3 className="font-semibold mb-2">View Reports</h3>
            <p className="text-sm text-gray-500 mb-4 flex-1">
              {truncateText(
                "Track your performance, analyze campaign metrics, and make data-driven decisions to grow your business.",
                20
              )}
            </p>
            <Link
              to="/company/dashboard/reports"
              className="bg-orange-500 text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
            >
              View Reports
            </Link>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
